import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';
import environment from '@environments/environment';
import {
  IrisSite,
  IrisWorkspace,
  IrisWorkspaceCreate,
  IrisWorkspaceType,
} from '@data-access/setup/types/iris-workspace.type';
import { IStudy } from '../study.interface';
import { IResults, ITaskList } from '@shared/iris.interface';
import { StudyDetailRecord } from '../study-details-facade.types';
import { SiteDetails } from '@data-access/sites';

import {
  IrisAttributeTypeResponse,
  IrisDocumentTypeResponse,
} from '../../setup/workspace/data-access/interfaces/iris-document.interface';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CUSTOM_ERROR_HANDLING } from '@shared/http';

@Injectable({
  providedIn: 'root',
})
export class IrisService {
  private readonly _http = inject(HttpClient);
  workspaceTypes: IrisWorkspaceType[] = [];
  hasPasswordProtectedDocument = false;
  newSubmissionSearchResults: any = [];
  private attributeTypesFetched = false;

  public uploadSubject = new BehaviorSubject<boolean>(false);
  public uploading$ = this.uploadSubject.asObservable();
  private attributeTypesSubject: BehaviorSubject<IrisAttributeTypeResponse | null> =
    new BehaviorSubject<IrisAttributeTypeResponse | null>(null);
  public attributeTypes$: Observable<IrisAttributeTypeResponse | null> =
    this.attributeTypesSubject.asObservable();

  private lastIds: { [key: string]: string | null } = {
    submissionId: null,
    studyId: null,
    siteId: null,
  };

  errorHandler(error: HttpErrorResponse): never {
    throw error;
  }

  getWorkspaces(pageIndex = 1, pageSize = 20) {
    return this._http.get<IrisWorkspace[]>(
      `${environment.irisApi}/workspace/?page=${pageIndex}&page_size=${pageSize}`,
    );
  }

  getReviewBoards() {
    const query = `${environment.irisApi}/workspace/review_boards/`;
    return this._http.get<any>(query);
  }

  searchWorkspaces(queryParams: HttpParams) {
    return this._http.get<{
      total: number;
      data: IrisWorkspace[];
    }>(`${environment.irisApi}/workspace/`, { params: queryParams });
  }

  async createStudy(study: any) {
    return this._http.post<IStudy>(`${environment.irisApi}/study/`, study);
  }

  getHolidays() {
    return this._http.get<IResults>(`${environment.irisApi}/holidays/`);
  }

  getWorkspaceTypes() {
    const isV2SubmissionEnabled = coerceBooleanProperty(
      'isNewSubmissionEnabled' in environment &&
        environment.isNewSubmissionEnabled,
    );
    const apiUrl = isV2SubmissionEnabled
      ? `${environment.irisApi}/v2/workspace/type/?page=1&page_size=100`
      : `${environment.irisApi}/workspace/type/?page=1&page_size=100`;
    return this._http.get<IResults<IrisWorkspaceType>>(apiUrl);
  }

  getWorkspaceSubTypes() {
    return this._http.get<any>(
      `${environment.irisApi}/workspace/sub_type/?page=1&page_size=100`,
    );
  }

  async getWorkspaceTypesBySearch(value: string) {
    return this._http.get<IResults<IrisWorkspace>>(
      `${environment.irisApi}/workspace/type/?query=${value}`,
    );
  }

  /**
   * Creates a workspace by calling V1 or V2 create endpoint, depending on
   * configured envirionment variable
   */
  createWorkspace(workspace: IrisWorkspaceCreate) {
    const newVersion = coerceBooleanProperty(environment.workspaceNewVersion);
    if (newVersion) {
      return this.createWorkspaceV2(workspace);
    }
    return this.createWorkspaceV1(workspace);
  }

  /** Creates a workspace by calling V1 create endpoint */
  private createWorkspaceV1(workspace: IrisWorkspaceCreate) {
    return this._http.post<IrisWorkspace>(
      `${environment.irisApi}/workspace/`,
      workspace,
    );
  }

  /** Creates a workspace by calling V2 create endpoint */
  private createWorkspaceV2(workspace: IrisWorkspaceCreate) {
    return this._http.post<IrisWorkspace>(
      `${environment.irisApi}/v2/workspace/`,
      workspace,
    );
  }

  async uploadDocument(document: FormData) {
    return this._http
      .post(`${environment.irisApi}/document/`, document, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(catchError(async (error) => this.errorHandler(error)));
  }

  async uploadAdditionalDocument(document: FormData) {
    const queryParams = new HttpParams().set('is_additional', true);
    return this._http
      .post(`${environment.irisApi}/document/`, document, {
        reportProgress: true,
        observe: 'events',
        params: queryParams,
      })
      .pipe(catchError(async (error) => this.errorHandler(error)));
  }
  searchSiteDocuments(siteId: string, queryParams: HttpParams) {
    return this._http.get(`${environment.irisApi}/site/${siteId}/documents/`, {
      params: queryParams,
    });
  }

  getAllStudies(queryParams: HttpParams) {
    return this._http.get<IResults>(`${environment.irisApi}/study/`, {
      params: queryParams,
    });
  }

  getReviewLocations() {
    return this._http.get<IResults>(`${environment.irisApi}/review_locations/`);
  }

  async getAllTaskList(workspace_id: string) {
    return this._http.get<ITaskList>(
      `${environment.irisApi}/workspace/${workspace_id}/task-list/`,
    );
  }

  getStudyOutcomeDocuments(study_id: string, queryParams: HttpParams) {
    return this._http.get<IResults>(
      `${environment.irisApi}/study/${study_id}/outcome-documents/`,
      { params: queryParams },
    );
  }

  exportWorkspaceDocuments(study_id: string, queryParams: HttpParams) {
    return this._http.get<any>(
      `${environment.irisApi}/workspace/${study_id}/documents/export/`,
      {
        params: queryParams,
        responseType: 'arraybuffer' as 'json',
        observe: 'response' as 'body',
      },
    );
  }

  getDocumentTypes(entity_id?: string, entity_type?: 'site' | 'study') {
    let url = `${environment.irisApi}/document/type/`;
    if (entity_id && entity_type) {
      url = `${url}?workspace_type_id=${entity_id}&entity_type=${entity_type}`;
    }

    return this._http.get<IrisDocumentTypeResponse>(url);
  }

  async getDocumentTypesByQuery(query?: string) {
    return this._http.get<IrisDocumentTypeResponse>(
      `${environment.irisApi}/document/type/?query=${query}`,
    );
  }

  getStudyDocuments(study_id: string, queryParams: HttpParams) {
    return this._http.get<IResults>(
      `${environment.irisApi}/study/${study_id}/documents/`,
      { params: queryParams },
    );
  }

  fetchAttributeTypes(type: string) {
    if (this.attributeTypesFetched) {
      return this.attributeTypesSubject.asObservable();
    }
    this.attributeTypesFetched = true;
    this._http
      .get<IrisAttributeTypeResponse>(
        `${environment.irisApi}/document/attribute-type/?entity_type=${type}`,
      )
      .subscribe(
        (response: IrisAttributeTypeResponse) => {
          this.attributeTypesSubject.next(response);
        },
        (error) => {
          console.error('Error fetching attribute types:', error);
          this.attributeTypesSubject.next(null);
        },
      );
    return this.attributeTypesSubject.asObservable();
  }

  getAttributeTypes(type: string) {
    if (!this.attributeTypesFetched) {
      this.fetchAttributeTypes(type);
    }
    return this.attributeTypesSubject.asObservable();
  }

  getStudyDetailById(studyId: string) {
    return this._http.get<StudyDetailRecord>(
      `${environment.irisApi}/study/${studyId}/`,
      {
        context: new HttpContext().set(CUSTOM_ERROR_HANDLING, 'silent-mode'),
      },
    );
  }

  async getConversationByIdAndType(entity_id: string, entity_type: string) {
    return this._http.get<any>(
      `${environment.irisApi}/conversation/${entity_type}/${entity_id}/`,
    );
  }

  createConversation(entity_id: string, entity_type: string) {
    return this._http.post<any>(`${environment.irisApi}/conversation/`, {
      entity_id,
      entity_type,
    });
  }

  async setWorkspaceStage(workspace_id: string) {
    return this._http.put<any>(
      `${environment.irisApi}/workspace/${workspace_id}/stage/`,
      {},
    );
  }

  requestAccessToStudy(body: { studyId: string; reason: string }) {
    return this._http.post<any>(
      `${environment.irisApi}/study/${body.studyId}/access/`,
      {
        access_type: 'write',
        reason: body.reason,
      },
    );
  }

  requestAccessToSite(body: { siteId: string; reason: string }) {
    return this._http.post<any>(
      `${environment.irisApi}/site/${body.siteId}/access/`,
      {
        access_type: 'write',
        reason: body.reason,
      },
    );
  }

  async revokeAccessToStudy(studyId: string, accessId: string) {
    return this._http.put<any>(
      `${environment.irisApi}/study/${studyId}/access/${accessId}/response/`,
      {
        status: 'rejected',
      },
    );
  }

  async acceptAccessToStudy(studyId: string, accessId: string) {
    return this._http.put<any>(
      `${environment.irisApi}/study/${studyId}/access/${accessId}/response/`,
      {
        status: 'granted',
      },
    );
  }

  setNestedValue(obj: any, path: string, value: any) {
    const pList = path.split('.');
    const len = pList.length;
    for (let i = 0; i < len - 1; i++) {
      const elem = pList[i];
      if (!obj[elem]) {
        obj[elem] = {};
      }
      obj = obj[elem];
    }

    obj[pList[len - 1]] = value;
    return obj;
  }

  // create a function that deletes a path from an object
  deleteNestedValue(obj: any, path: string) {
    const pList = path.split('.');
    const len = pList.length;
    for (let i = 0; i < len - 1; i++) {
      const elem = pList[i];
      if (!obj[elem]) {
        obj[elem] = {};
      }
      obj = obj[elem];
    }

    delete obj[pList[len - 1]];
    return obj;
  }

  async updateAnswerDocuments(answer: FormData) {
    const queryParams = new HttpParams().set('is_document', true);
    return this._http
      .put(`${environment.irisApi}/answer/`, answer, {
        reportProgress: true,
        observe: 'events',
        params: queryParams,
      })
      .pipe(catchError(async (error) => this.errorHandler(error)));
  }

  deleteAnswerDocuments(answer_id: string, document_id: string) {
    return this._http.delete<any>(
      `${environment.irisApi}/answer/${answer_id}/document/${document_id}/`,
    );
  }

  async updateAnswers(answers: any) {
    return this._http.put<any>(`${environment.irisApi}/answer/`, answers);
  }

  async updateQuestionAnswerTags(answerId: string, tags: any) {
    return this._http.put<any>(
      `${environment.irisApi}/answer/${answerId}/tag/`,
      tags,
    );
  }

  getSiteSearch(body: {
    wcg_tracking_id: string;
    pi_first_name: string;
    pi_last_name: string;
  }) {
    const encodedId = encodeURIComponent(body.wcg_tracking_id);
    return this._http.get<{
      pi_first_name: string;
      pi_last_name: string;
      sponsor_name: string;
      sponsor_protocol_id: string;
      wcg_tracking_id: string;
    }>(
      `${environment.irisApi}/site/access/search/?query=${encodedId}&pi_first_name=${body.pi_first_name}&pi_last_name=${body.pi_last_name}`,
    );
  }
  searchStudyAccess(wcg_tracking_id: any) {
    const encodedId = encodeURIComponent(wcg_tracking_id);
    return this._http.get<any>(
      `${environment.irisApi}/study/access/search/?query=${encodedId}`,
    );
  }

  async getAllDocumentListNoPage(workspace_type_id: string) {
    return this._http.get<any>(
      `${environment.irisApi}/document/required_document_type/?workspace_type_id=${workspace_type_id}&page_size=100`,
    );
  }

  getAllDocumentList(workspace_type_id: string) {
    return this._http.get<any>(
      `${environment.irisApi}/document/required_document_type/?workspace_type_id=${workspace_type_id}`,
    );
  }

  async getReviewerMaterialDocumentTypesList(pageIndex = 1, pageSize = 10) {
    return this._http.get<any>(
      `${environment.irisApi}/reviewer-document-type/?page=${pageIndex}&page_size=${pageSize}`,
    );
  }

  async getReviewerMaterialDocuments(
    workspace_id: string,
    pageIndex = 1,
    pageSize = 20,
  ) {
    return this._http.get<any>(
      `${environment.irisApi}/workspace/${workspace_id}/reviewer-document/?page=${pageIndex}&page_size=${pageSize}`,
    );
  }

  async updateReviewerMaterialDocument(
    reviewer_document_id: string,
    reviewer_document: any,
  ) {
    return this._http.put<any>(
      `${environment.irisApi}/reviewer-document/${reviewer_document_id}/`,
      reviewer_document,
    );
  }

  async getReviewerMaterialDocument(document_id: string) {
    return this._http.get<any>(
      `${environment.irisApi}/reviewer-document/${document_id}/`,
    );
  }

  async deleteReviewerMaterialDocument(id: string) {
    return this._http.delete<any>(
      `${environment.irisApi}/reviewer-document/${id}/`,
    );
  }

  async acknowledgeDocumentRequest(messageId: string) {
    return this._http.put<any>(`${environment.irisApi}/message/${messageId}/`, {
      is_acknowledged: true,
    });
  }

  uploadReviewMaterial(document: FormData) {
    return this._http
      .post(`${environment.irisApi}/reviewer-document/`, document, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(catchError(async (error) => this.errorHandler(error)));
  }

  async addUserToWorkspace(workspaceId: string) {
    return this._http.post<any>(
      `${environment.irisApi}/workspace/${workspaceId}/user/`,
      {},
    );
  }
  /**
   * @TODO:- Will eventually be removed once the components are updated with
   * @NGRX SIGNAL STORE.
   * */
  getCountries(pageIndex = 1, pageSize = 300) {
    return this._http.get<any>(
      `${environment.irisApi}/country/?page=${pageIndex}&page_size=${pageSize}`,
    );
  }

  getStates(countryId: string, pageIndex = 1, pageSize = 200) {
    return this._http.get<any>(
      `${environment.irisApi}/state/?country_id=${countryId}&page=${pageIndex}&page_size=${pageSize}`,
    );
  }

  createSite(studyId: string, body: any) {
    return this._http.post<any>(
      `${environment.irisApi}/study/${studyId}/site/`,
      body,
    );
  }

  checkStudyInvestigatorRelationship(payload: any) {
    return this._http.post<any>(
      `${environment.irisApi}/investigator/association/`,
      payload,
    );
  }

  addFacilityLocations(siteId: string, data: FormData) {
    return this._http
      .post(`${environment.irisApi}/site/${siteId}/address/`, data)
      .pipe(catchError((error) => of(this.errorHandler(error))));
  }

  getSiteDetailsById(siteId: string): Observable<SiteDetails> {
    return this._http.get<SiteDetails>(
      `${environment.irisApi}/site/${siteId}/`,
      {
        context: new HttpContext().set(CUSTOM_ERROR_HANDLING, 'silent-mode'),
      },
    );
  }

  getSites(pageIndex = 1, pageSize = 100) {
    return this._http.get<{ data: IrisSite[]; total: number }>(
      `${environment.irisApi}/site/?page=${pageIndex}&page_size=${pageSize}`,
    );
  }

  async searchSites(query: string, pageIndex = 1, pageSize = 100) {
    return this._http.get<IrisSite[]>(
      `${environment.irisApi}/site/?page=${pageIndex}&page_size=${pageSize}&query=${query}`,
    );
  }
  getFacilityLocations(siteId: string, query: string) {
    return this._http.get<any>(
      `${environment.irisApi}/site/${siteId}/address/${query}`,
    );
  }
  getInvestigatorLocations(contactId: string, siteId: string) {
    return this._http.get<any>(
      `${environment.irisApi}/contact/${contactId}/locations/?site_id=${siteId}`,
    );
  }
  getWorkspaceDetails(workspaceId: string) {
    return this._http.get<IrisWorkspace>(
      `${environment.irisApi}/workspace/${workspaceId}/`,
    );
  }
  updateSelectedFacilityLocations(siteId: string, data: any) {
    return this._http
      .put(`${environment.irisApi}/site/${siteId}/location/`, data)
      .pipe(catchError((error) => of(this.errorHandler(error))));
  }

  batchRemoveSMEQC(
    attributes: { attribute: string; user_id: string; workspace_id: string }[],
  ) {
    return this._http.delete<{ message: string }>(
      `${environment.irisApi}/workspace/batch-user-attributes/`,
      {
        body: { attributes },
      },
    );
  }

  getDegreeList() {
    return this._http.get<string[]>(`${environment.irisApi}/degrees/`);
  }

  getSpecialtyList() {
    return this._http.get<string[]>(`${environment.irisApi}/specialties/`);
  }

  getAllOrganizations() {
    return this._http.get<any[]>(`${environment.irisApi}/organization/`);
  }

  getWorkspacesByStudyId(studyId: string, queryParams: HttpParams) {
    return this._http.get<any>(
      `${environment.irisApi}/study/${studyId}/workspaces/`,
      { params: queryParams },
    );
  }

  getStudySites(study_id: string, queryParams: HttpParams) {
    return this._http.get<IResults>(
      `${environment.irisApi}/study/${study_id}/site/`,
      { params: queryParams },
    );
  }

  setLastId(key: 'submissionId' | 'studyId' | 'siteId', id: string): void {
    this.lastIds[key] = id;
  }
}
